/**
 * Config Loader
 *
 * Loads AppConfig from multiple sources:
 * - JSON file (local development)
 * - REST API endpoint (production)
 * - Cloudflare KV (edge deployment)
 */

import { appConfigSchema, shadcnAppConfigSchema } from './config-validator';
import type { AppConfig, ShadcnAppConfig } from '../types/app-config';

// =============================================================================
// Config Source Types
// =============================================================================

export type ConfigSource =
  | { type: 'json'; url: string }
  | { type: 'api'; url: string; headers?: Record<string, string> }
  | { type: 'kv'; namespaceId: string; key: string; accountId: string; apiToken: string }
  | { type: 'pages'; appId: string };

// =============================================================================
// Loader
// =============================================================================

export async function loadAppConfig(source: ConfigSource): Promise<AppConfig | ShadcnAppConfig> {
  let raw: unknown;

  switch (source.type) {
    case 'json': {
      const resp = await fetch(source.url);
      if (!resp.ok) {
        throw new Error(`Failed to load config from ${source.url}: ${resp.status} ${resp.statusText}`);
      }
      raw = await resp.json();
      break;
    }

    case 'api': {
      const resp = await fetch(source.url, {
        headers: {
          'Accept': 'application/json',
          ...source.headers,
        },
      });
      if (!resp.ok) {
        throw new Error(`Failed to load config from API ${source.url}: ${resp.status} ${resp.statusText}`);
      }
      raw = await resp.json();
      break;
    }

    case 'kv': {
      const kvUrl = `https://api.cloudflare.com/client/v4/accounts/${source.accountId}/storage/kv/namespaces/${source.namespaceId}/values/${source.key}`;
      const resp = await fetch(kvUrl, {
        headers: {
          'Authorization': `Bearer ${source.apiToken}`,
        },
      });
      if (!resp.ok) {
        throw new Error(`Failed to load config from KV: ${resp.status} ${resp.statusText}`);
      }
      raw = await resp.json();
      break;
    }

    case 'pages': {
      const resp = await fetch(`/api/config/${source.appId}`);
      if (!resp.ok) {
        throw new Error(
          `Failed to load config for "${source.appId}": ${resp.status} ${resp.statusText}`,
        );
      }
      raw = await resp.json();
      break;
    }
  }

  // Validate with Zod (shadcn configs use a stricter schema)
  const isShadcnConfig = typeof raw === 'object' && raw !== null && (raw as { shadcn?: unknown }).shadcn === true;
  const result = isShadcnConfig
    ? shadcnAppConfigSchema.safeParse(raw)
    : appConfigSchema.safeParse(raw);

  if (!result.success) {
    const issues = result.error.issues.map(i => `  - ${i.path.join('.')}: ${i.message}`).join('\n');
    const configType = isShadcnConfig ? 'ShadcnAppConfig' : 'AppConfig';
    throw new Error(`Invalid ${configType}:\n${issues}`);
  }

  // Normalize ShadcnAppConfig: add id/name/presetId aliases for legacy code
  if (isShadcnConfig) {
    const data = result.data as Record<string, unknown>;
    if (!data['id'] && data['appId']) data['id'] = data['appId'];
    if (!data['name'] && data['appName']) data['name'] = data['appName'];
    // Map theme.presetName to a registry preset ID so PresetProvider doesn't crash
    if (!data['presetId']) {
      const theme = data['theme'] as Record<string, unknown> | undefined;
      const presetName = theme?.['presetName'] as string | undefined;
      const PRESET_ID_MAP: Record<string, string> = {
        'executive': 'saas-executive-premium',
        'grafana-dark': 'analytics-grafana-dark',
        'agency-creative': 'marketing-agency-creative',
        'saas-modern-minimal': 'saas-modern-minimal',
        'saas-data-dense': 'saas-data-dense',
        'saas-enterprise-clean': 'saas-enterprise-clean',
        'saas-startup-fresh': 'saas-startup-fresh',
      };
      data['presetId'] = (presetName && PRESET_ID_MAP[presetName]) ?? PRESET_ID_MAP[presetName ?? ''] ?? 'saas-modern-minimal';
    }
  }

  return result.data as AppConfig | ShadcnAppConfig;
}
/**
 * Helper to create a ConfigSource from environment variables.
 * Works with Vite's import.meta.env pattern.
 */
export function resolveConfigSource(env: Record<string, string | undefined>): ConfigSource {
  const sourceType = env.VITE_APP_CONFIG_SOURCE || 'json';
  const appId = env.VITE_APP_ID || 'clientforce';

  switch (sourceType) {
    case 'pages':
      return {
        type: 'pages',
        appId,
      };

    case 'kv':
      return {
        type: 'kv',
        namespaceId: env.VITE_CF_KV_NAMESPACE || '',
        key: appId,
        accountId: env.VITE_CF_ACCOUNT_ID || '',
        apiToken: env.VITE_CF_API_TOKEN || '',
      };

    case 'api':
      return {
        type: 'api',
        url: `${env.VITE_CONFIG_API_URL || ''}/apps/${appId}`,
      };

    default:
      return {
        type: 'json',
        url: `/configs/${appId}.json`,
      };
  }
}
